// AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md (besluiten R1–R3)
import toMillis from '../measurementSignals/toMillis'
import { horizonFromDose } from './applyRekenregels'

// Duur en intensiteit van de deelname, nodig voor de tijdshorizon van de
// rekenregels. Bron per veld: het blok Traject van de deelnemer (tab Gegevens:
// 'Startdatum', 'Einddatum', 'Uren per week'); ontbreekt een veld, dan de
// standaard van de organisatie (`CompagnyMeta.DefaultInterventionDose`). Is er
// niets, dan geen horizon maar een reden — de waarde telt dan niet mee
// (besluit R2), er komt nooit een stille standaard.
//
// Spiegel: functions/msiMonetisationHelpers/resolveInterventionDose.js,
// bewaakt door resolveInterventionDose.parity.test.js.

const DAY_MS = 86400000
// Gemiddelde kalendermaand, zelfde waarde als het meetstandaardrapport.
const DAYS_PER_MONTH = 30.44

/**
 * @typedef {'deelnemer'|'organisatie'|null} DoseSource
 */

/**
 * @typedef {object} InterventionDose
 * @property {number|null} months - Duur van de deelname in kalendermaanden
 * @property {number|null} hoursPerWeek - Gemiddelde deelname-uren per week
 * @property {number|null} horizon - Tijdshorizon in jaren, null = niet doorrekenen
 * @property {{ months: DoseSource, hours: DoseSource }} source - Waar elk veld vandaan kwam
 * @property {'duur onbekend'|'uren onbekend'|'einde vóór start'|null} reason - Waarom er geen horizon is
 */

/**
 * Leest 'Uren per week' zoals het opgeslagen staat (tekst uit een
 * getalveld, soms met komma). 0 is een echt antwoord.
 * @param {*} value
 * @returns {number|null}
 */
const parseHours = (value) => {
    if (value === '' || value === null || value === undefined) return null
    const n = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'))
    return Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * Maanden tussen start en eind van het traject. Zonder einddatum telt de
 * laatste meting als eind (besluit R3), niet vandaag.
 * @returns {{ months: number|null, invalid: boolean }}
 */
const monthsOfTraject = (persona, lastMeasurementMs) => {
    const start = toMillis(persona?.StartDate)
    const end = toMillis(persona?.EndDate) ?? (Number.isFinite(lastMeasurementMs) ? lastMeasurementMs : null)
    if (start === null || end === null) return { months: null, invalid: false }
    if (end < start) return { months: null, invalid: true }
    return { months: (end - start) / DAY_MS / DAYS_PER_MONTH, invalid: false }
}

/**
 * @param {object} [input]
 * @param {{ StartDate?: *, EndDate?: *, HoursPerWeek?: * }|null} [input.persona] - Personas-doc, null bij een anonieme respondent
 * @param {number|null} [input.lastMeasurementMs] - Datum van de laatste meting in het verschil
 * @param {{ Months?: *, HoursPerWeek?: * }|null} [input.orgDefault] - CompagnyMeta.DefaultInterventionDose
 * @returns {InterventionDose}
 */
const resolveInterventionDose = ({ persona = null, lastMeasurementMs = null, orgDefault = null } = {}) => {
    const traject = monthsOfTraject(persona, lastMeasurementMs)
    if (traject.invalid) {
        return { months: null, hoursPerWeek: null, horizon: null, source: { months: null, hours: null }, reason: 'einde vóór start' }
    }

    const personaHours = parseHours(persona?.HoursPerWeek)
    const defaultMonths = parseHours(orgDefault?.Months)
    const defaultHours = parseHours(orgDefault?.HoursPerWeek)

    const months = traject.months ?? defaultMonths
    const hoursPerWeek = personaHours ?? defaultHours
    const source = {
        months: traject.months !== null ? 'deelnemer' : defaultMonths !== null ? 'organisatie' : null,
        hours: personaHours !== null ? 'deelnemer' : defaultHours !== null ? 'organisatie' : null,
    }
    const reason = months === null ? 'duur onbekend' : hoursPerWeek === null ? 'uren onbekend' : null

    return { months, hoursPerWeek, horizon: reason ? null : horizonFromDose({ months, hoursPerWeek }), source, reason }
}

export default resolveInterventionDose
export { parseHours }
